/**
 * BA-55's tap guard for a confirm button: it acts only on a press that began at least
 * TAP_GUARD_MS after the dialog opened (or the guard was re-armed). A keyboard activation
 * (detail 0) has no press: it only waits for the guard. Cancel is never guarded, but a
 * backdrop click inside the window is ignored: on a desktop the second click of a double
 * tap on the opening button lands on the backdrop, and must not close the dialog.
 */

import { useCallback, useMemo, useRef } from 'react';

export const TAP_GUARD_MS = 500;

export interface TapGuard {
  /** Start the guard: when the dialog opens, or when its meaning changes */
  arm: () => void;
  onPointerDown: () => void;
  /** Whether this click may act; call it once per click */
  accept: (event: { detail: number }) => boolean;
  /** Whether the guard window is still running: a dialog ignores backdrop clicks in it */
  guarding: () => boolean;
}

export function useTapGuard(): TapGuard {
  const guardEndsAt = useRef(0);
  const pressArmed = useRef(false);

  const arm = useCallback(() => {
    guardEndsAt.current = performance.now() + TAP_GUARD_MS;
    pressArmed.current = false;
  }, []);

  const onPointerDown = useCallback(() => {
    pressArmed.current = performance.now() >= guardEndsAt.current;
  }, []);

  const accept = useCallback((event: { detail: number }) => {
    const ready = event.detail === 0
      ? performance.now() >= guardEndsAt.current
      : pressArmed.current;
    pressArmed.current = false;
    return ready;
  }, []);

  const guarding = useCallback(() => performance.now() < guardEndsAt.current, []);

  return useMemo(() => ({ arm, onPointerDown, accept, guarding }), [arm, onPointerDown, accept, guarding]);
}
